import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import SessionManager from "./Utils/SessionManager";
import myToast from "./Utils/MyToast";
import COLORS from "./resources/colors/COLORS";

const ACCENT = COLORS.primary_color_shade6;
const ACCENT_DARK = COLORS.primary_color_shade8;
const ACCENT_SOFT = COLORS.primary_color_shade0;

const API_BASE = () =>
  process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR;

//Created By Cursor
function getDefaultSessionDates() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month < 4) {
    return {
      start_date: `${year - 1}-04-01`,
      end_date: `${year}-03-31`,
      session_name: `${year - 1}-${year}`,
    };
  }
  return {
    start_date: `${year}-04-01`,
    end_date: `${year + 1}-03-31`,
    session_name: `${year}-${year + 1}`,
  };
}

//Created By Cursor
function deriveSessionName(startDate, endDate) {
  const startYear = String(startDate || "").slice(0, 4);
  const endYear = String(endDate || "").slice(0, 4);
  if (startYear && endYear) return `${startYear}-${endYear}`;
  return "";
}

//Created By Cursor
function SessionSetting() {
  const history = useHistory();
  const location = useLocation();
  const defaults = useMemo(() => getDefaultSessionDates(), []);

  const schoolCodeFromState = location.state?.school_code || "";
  const schoolNameFromState = location.state?.school_name || "";
  const schoolCode =
    schoolCodeFromState || SessionManager.getSchoolCode() || "";

  const [boards, setBoards] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    session_name: defaults.session_name,
    start_date: defaults.start_date,
    end_date: defaults.end_date,
    board_name: "",
    medium_name: "",
    student_id_prefix: "S",
    student_id_start_from: "1",
    student_id_start_digit: "6",
    staff_id_prefix: "STF",
    staff_id_start_from: "1",
    staff_id_start_digit: "4",
    admission_no: "1",
  });

  useEffect(() => {
    const id = "eduok-login-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Source+Sans+3:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  //Created By Cursor
  const loadMasters = useCallback(async () => {
    setLoadingMasters(true);
    try {
      const [boardRes, mediumRes, classRes] = await Promise.all([
        axios.get(API_BASE() + "getBoardMaster.php?"),
        axios.get(API_BASE() + "getMediumMaster.php?"),
        axios.get(API_BASE() + "getClassMaster.php?"),
      ]);

      if (boardRes?.data?.api_status === "OK") {
        setBoards(boardRes.data.data || []);
      }
      if (mediumRes?.data?.api_status === "OK") {
        setMediums(mediumRes.data.data || []);
      }
      if (classRes?.data?.api_status === "OK") {
        const list = (classRes.data.data || []).map((item) => ({
          ...item,
          selected: "false",
        }));
        setClasses(list);
      }
    } catch (error) {
      console.error("SessionSetting loadMasters:", error);
      myToast.notify_warning("Failed to load session setup data.");
    } finally {
      setLoadingMasters(false);
    }
  }, []);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "start_date" || name === "end_date") {
        next.session_name = deriveSessionName(
          name === "start_date" ? value : next.start_date,
          name === "end_date" ? value : next.end_date
        );
      }
      return next;
    });
  }, []);

  //Created By Cursor
  const toggleClass = useCallback((index) => {
    setClasses((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              selected: item.selected === "true" ? "false" : "true",
            }
          : item
      )
    );
  }, []);

  //Created By Cursor
  const selectAllClasses = useCallback((selected) => {
    setClasses((prev) =>
      prev.map((item) => ({ ...item, selected: selected ? "true" : "false" }))
    );
  }, []);

  //Created By Cursor
  const validate = useCallback(() => {
    if (!schoolCode) {
      myToast.notify_warning("School code missing. Please register again.");
      return false;
    }
    if (!form.session_name.trim()) {
      myToast.notify_warning("Session name is required");
      return false;
    }
    if (!form.start_date || !form.end_date) {
      myToast.notify_warning("Select session start and end dates");
      return false;
    }
    if (!form.board_name) {
      myToast.notify_warning("Select Board");
      return false;
    }
    if (!form.medium_name) {
      myToast.notify_warning("Select Medium");
      return false;
    }
    if (!form.admission_no.trim()) {
      myToast.notify_warning("Admission Number is Required");
      return false;
    }
    if (!classes.some((c) => c.selected === "true")) {
      myToast.notify_warning("Select Class");
      return false;
    }
    return true;
  }, [schoolCode, form, classes]);

  //Created By Cursor
  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!validate()) return;

      setSubmitting(true);
      try {
        const params = {
          session_name: form.session_name.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          board_name: form.board_name,
          medium_name: form.medium_name,
          student_id_prefix: form.student_id_prefix.trim(),
          student_id_start_from: form.student_id_start_from.trim(),
          student_id_start_digit: form.student_id_start_digit.trim(),
          staff_id_prefix: form.staff_id_prefix.trim(),
          staff_id_start_from: form.staff_id_start_from.trim(),
          staff_id_start_digit: form.staff_id_start_digit.trim(),
          admission_no: form.admission_no.trim(),
          strClass: JSON.stringify(classes),
          school_code: schoolCode,
        };

        const response = await axios.get(API_BASE() + "AddNewSession.php?", {
          params,
        });

        if (response?.data?.api_status === "OK" && response.data.staff_id) {
          myToast.notify_success("Successfully Submitted");
          history.push({
            pathname: "/SchoolRegistrationSuccess",
            state: {
              staff_id: response.data.staff_id,
              school_code: schoolCode,
              school_name: schoolNameFromState,
            },
          });
        } else {
          myToast.notify_warning(
            response?.data?.api_remark || "Error in Submission"
          );
        }
      } catch (error) {
        console.error("SessionSetting submit:", error);
        myToast.notify_warning(
          error.response?.data?.api_remark ||
            "Network error. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
    [validate, form, classes, schoolCode, schoolNameFromState, history]
  );

  if (!schoolCode) {
    return <Redirect to="/Register" />;
  }

  const inputBase = {
    width: "100%",
    height: 46,
    padding: "0 12px",
    borderRadius: 10,
    border: "1.5px solid #d0d7e2",
    background: "#fff",
    fontFamily: '"Source Sans 3", sans-serif',
    fontSize: 14,
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 6,
  };

  const sectionTitle = {
    margin: "0 0 12px",
    fontFamily: '"Outfit", sans-serif',
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: '"Source Sans 3", sans-serif',
        background: `linear-gradient(145deg, ${ACCENT_SOFT} 0%, #ffffff 42%, #e0f2fe 100%)`,
        padding: "28px 16px 40px",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .eduok-ss-input:focus {
          border-color: ${ACCENT} !important;
          box-shadow: 0 0 0 3px ${ACCENT_SOFT} !important;
        }
        .eduok-ss-btn:hover:not(:disabled) {
          background: ${ACCENT_DARK} !important;
          transform: translateY(-1px);
        }
        .eduok-ss-class:hover {
          border-color: ${ACCENT} !important;
        }
      `}</style>

      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/logo.svg`}
              alt="EduOK"
              style={{ width: 42, height: 42, borderRadius: 10 }}
            />
            <div>
              <div
                style={{
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: 24,
                  fontWeight: 800,
                  color: ACCENT_DARK,
                  letterSpacing: "-0.02em",
                }}
              >
                Edu<span style={{ color: ACCENT }}>OK</span>
              </div>
              <div style={{ fontSize: 13, color: "#64748b" }}>
                Session setup
                {schoolNameFromState ? ` · ${schoolNameFromState}` : ""}
              </div>
            </div>
          </div>
          <Link
            to="/Register"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: ACCENT,
              textDecoration: "none",
            }}
          >
            Back
          </Link>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.96)",
            border: "1px solid #d7e6f4",
            borderRadius: 18,
            boxShadow: "0 16px 40px rgba(1, 87, 155, 0.10)",
            padding: "28px 26px",
          }}
        >
          <p
            style={{
              margin: "0 0 6px",
              fontFamily: '"Outfit", sans-serif',
              fontSize: 22,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Academic Session Settings
          </p>
          <p style={{ margin: "0 0 22px", fontSize: 14, color: "#64748b" }}>
            Configure your first session, board, medium, and classes.
          </p>

          {loadingMasters ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#64748b",
              }}
            >
              <i className="fa fa-spinner fa-spin" aria-hidden /> Loading
              setup…
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ padding: 0 }}>
              <p style={sectionTitle}>Session dates</p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <div>
                  <label style={labelStyle}>Session Name</label>
                  <input
                    className="eduok-ss-input"
                    name="session_name"
                    value={form.session_name}
                    onChange={handleChange}
                    style={inputBase}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <input
                    type="date"
                    className="eduok-ss-input"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                    style={inputBase}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <input
                    type="date"
                    className="eduok-ss-input"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleChange}
                    style={inputBase}
                    required
                  />
                </div>
              </div>

              <p style={sectionTitle}>Board & Medium</p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <div>
                  <label style={labelStyle}>Board</label>
                  <select
                    className="eduok-ss-input"
                    name="board_name"
                    value={form.board_name}
                    onChange={handleChange}
                    style={{ ...inputBase, cursor: "pointer" }}
                    required
                  >
                    <option value="">Select Board</option>
                    {boards.map((b) => (
                      <option key={b.id || b.board_name} value={b.board_name}>
                        {b.board_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Medium</label>
                  <select
                    className="eduok-ss-input"
                    name="medium_name"
                    value={form.medium_name}
                    onChange={handleChange}
                    style={{ ...inputBase, cursor: "pointer" }}
                    required
                  >
                    <option value="">Select Medium</option>
                    {mediums.map((m) => (
                      <option key={m.id || m.medium_name} value={m.medium_name}>
                        {m.medium_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 20, maxWidth: 220 }}>
                <label style={labelStyle}>Admission No. Start From</label>
                <input
                  className="eduok-ss-input"
                  name="admission_no"
                  value={form.admission_no}
                  onChange={handleChange}
                  style={inputBase}
                  inputMode="numeric"
                  required
                />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <p style={{ ...sectionTitle, margin: 0 }}>Select Classes</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => selectAllClasses(true)}
                    style={{
                      border: `1px solid ${ACCENT}`,
                      background: "#fff",
                      color: ACCENT_DARK,
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => selectAllClasses(false)}
                    style={{
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#64748b",
                      borderRadius: 8,
                      padding: "6px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
                  gap: 10,
                  marginBottom: 24,
                }}
              >
                {classes.map((item, index) => {
                  const selected = item.selected === "true";
                  return (
                    <button
                      key={`${item.class_name}-${index}`}
                      type="button"
                      className="eduok-ss-class"
                      onClick={() => toggleClass(index)}
                      style={{
                        minHeight: 52,
                        borderRadius: 10,
                        border: selected
                          ? `2px solid ${ACCENT}`
                          : "1.5px solid #d0d7e2",
                        background: selected ? ACCENT_SOFT : "#fff",
                        color: selected ? ACCENT_DARK : "#64748b",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease, background 0.15s ease",
                      }}
                    >
                      {item.class_name}
                    </button>
                  );
                })}
              </div>

              <button
                type="submit"
                className="eduok-ss-btn"
                disabled={submitting}
                style={{
                  width: "100%",
                  height: 52,
                  border: "none",
                  borderRadius: 10,
                  background: ACCENT,
                  color: "#fff",
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.75 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  transition: "background 0.2s ease, transform 0.2s ease",
                }}
              >
                {submitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin" aria-hidden />
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit & Next
                    <i className="fa fa-arrow-right" aria-hidden />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default SessionSetting;
